import {
  AfterViewInit,
  Component,
  effect,
  inject,
  OnDestroy,
  Signal,
  ModelSignal,
  model,
  untracked
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { RqaMapService } from '../services/rqa-map.service';
import { AirspaceElement, MapLayerTypeEnum, WebrqaGeoJSONOptions } from 'src/app/model/map.model';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { AirspaceInfoComponent } from './airspace-info/airspace-info.component';
import { AirspaceTableComponent } from './airspace-table/airspace-table.component';
import { MapToolbarComponent } from 'src/app/map/map-toolbar/map-toolbar.component';
import * as L from 'leaflet';
import { MapToolbarAction, MapToolbarPayload } from 'src/app/map/map-toolbar/map-toolbar-actions';
import { AdhocService } from 'src/app/services/adhoc.service';
import { circleToPolygon, convertCoordinate } from 'src/app/shared/utils/coordinates.utils';
import { environment } from 'src/environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IPoint } from 'src/app/model/adhoc.model';
import { StyleType } from 'src/app/model/layers.model';
import { Router } from '@angular/router';
import { MapHeightComponent } from 'src/app/map/map-height/map-height.component';
import { altitudeArrayObserver } from 'src/app/model/altitudes.model';
import { NavigationStart } from '@angular/router';
import { filter } from 'rxjs';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { AccordionTabOpenEvent } from 'primeng/accordion';
import { MapOperationsService } from 'src/app/map/services/map-operations.service';
import { MapLayerService } from 'src/app/map/services/map-layer.service';
import { LayoutUtilsService } from 'src/app/services/layout-utils.service';
import { RqaLayerCreatorService } from 'src/app/map/services/rqa-layer-creator.service';
import { MapToolbarService } from '../services/map-toolbar.service';

@Component({
  selector: 'rqa-create-map',
  templateUrl: './rqa-map.component.html',
  styleUrls: ['./rqa-map.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    LeafletModule,
    AccordionModule,
    TooltipModule,
    ButtonModule,
    AirspaceInfoComponent,
    AirspaceTableComponent,
    MapToolbarComponent,
    MapHeightComponent
  ]
})
export class RqaCreateMapComponent implements AfterViewInit, OnDestroy {
  private rqaMapService = inject(RqaMapService);
  private toolbarService = inject(MapToolbarService);
  private adhocService = inject(AdhocService);
  private router = inject(Router);
  private mapOperationsService = inject(MapOperationsService);
  private mapLayerService = inject(MapLayerService);
  private layoutUtilsService = inject(LayoutUtilsService);
  private rqaLayerCreatorService = inject(RqaLayerCreatorService);

  readonly rqaMapLayers: Signal<AirspaceElement[]> = this.rqaMapService.mapLayers;
  readonly toolbarAction: Signal<{ action: MapToolbarAction; payload?: MapToolbarPayload } | null> =
    this.toolbarService.actionSignal;

  // Map-related signals from services
  readonly currentMarker = this.mapOperationsService.currentMarker;
  readonly groupedLayers = this.mapLayerService.groupedLayers;
  readonly filterLayerTypes = this.mapLayerService.filterLayerTypes;
  readonly showFilters = this.mapLayerService.showFilters;
  readonly disabledAreas = this.mapLayerService.disabledAreas;
  readonly contentHeight = this.layoutUtilsService.contentHeight;

  activeAccordionIndex: ModelSignal<number | null> = model<number | null>(null);
  selectedLayers: AirspaceElement[] = [];
  fetchLayersCount = 0;
  drawnItems = new L.FeatureGroup();
  heightRange: string[] = [altitudeArrayObserver[0], altitudeArrayObserver[altitudeArrayObserver.length - 1]];

  private isMapReady = false;
  private isProcessingLayerClick = false;

  // Map options
  options: L.MapOptions = {
    layers: [L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: 'Open Street Map' })],
    zoom: 6,
    minZoom: 5,
    maxZoom: 17,
    center: new L.LatLng(environment.center[0], environment.center[1]),
    zoomControl: false
  };

  constructor() {
    // Load disabled areas once on init
    this.mapLayerService.getDisabledAreas();

    // Effect only for RQA layers - simplified
    effect(() => {
      const layers = this.rqaMapLayers();

      if (this.isMapReady && layers.length > 0) {
        untracked(() => {
          this.handleLayersChanged(layers);
        });
      }
    });

    // Effect only for toolbar actions
    effect(() => {
      const action = this.toolbarAction();
      if (action) {
        untracked(() => {
          this.handleToolbarAction(action);
          this.toolbarService.clearAction();
        });
      }
    });

    // Effect only for accordion
    effect(() => {
      const index = this.activeAccordionIndex();
      untracked(() => {
        this.handleAccordionToggle(index);
      });
    });

    // Effect only for map resize
    effect(() => {
      const mapResize = this.mapOperationsService.resizeMap();
      if (mapResize && this.isMapReady) {
        untracked(() => {
          setTimeout(() => {
            const map = this.mapOperationsService.mapInstance();
            if (map) {
              map.invalidateSize(true);
            }
          }, 300);
        });
      }
    });

    // Setup RxJS subscriptions in constructor
    this.setupSubscriptions();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.resetAccordionState();
        this.rqaMapService.resetLayerSelection();
      });
  }

  private setupSubscriptions(): void {
    // Listen for layer selection from table
    this.rqaMapService
      .getLayerSelect()
      .pipe(takeUntilDestroyed())
      .subscribe((selectedLayer) => {
        this.selectedLayers = [];
        this.mapLayerService.resetLayerStyles();
        if (selectedLayer) {
          this.selectedLayers = this.rqaMapLayers().filter((el: AirspaceElement) => el.designator === selectedLayer);
          const map = this.mapOperationsService.mapInstance();
          if (map && this.isMapReady) {
            map.eachLayer((layer) => {
              const layerOptions = (layer as L.GeoJSON).options as WebrqaGeoJSONOptions;
              if (layerOptions.designator === selectedLayer) {
                this.onLayerClick(layer as L.GeoJSON);
                this.selectedLayers = [layer.options as AirspaceElement];
              }
            });
          }
        }
        this.activeAccordionIndex.set(this.selectedLayers.length === 1 ? 0 : null);
      });

    // Listen for circle drawing
    this.adhocService
      .getCircleToDraw()
      .pipe(takeUntilDestroyed())
      .subscribe((circleData) => {
        this.cleanDrawLayers();
        const map = this.mapOperationsService.mapInstance();
        if (circleData && map && this.isMapReady) {
          const latLngConverted = new L.LatLng(
            convertCoordinate(circleData.latitude),
            convertCoordinate(circleData.longitude)
          );
          const circleLayer = L.circle(latLngConverted, { radius: circleData.radius });
          circleLayer.addTo(map);
          this.drawnItems.addLayer(circleLayer);
          try {
            map.fitBounds(circleLayer.getBounds());
          } catch (error) {
            console.warn('Failed to fit circle bounds:', error);
          }
          this.adhocService.setGeoJson(circleToPolygon(circleLayer));
        }
      });

    // Listen for polygon drawing
    this.adhocService
      .getPolyToDraw()
      .pipe(takeUntilDestroyed())
      .subscribe((polyData) => {
        this.cleanDrawLayers();
        const latLng: L.LatLng[] = [];
        const map = this.mapOperationsService.mapInstance();
        if (polyData && map && this.isMapReady) {
          polyData.points.forEach((point: IPoint) => {
            if (point.lat && point.lon) {
              const coords = new L.LatLng(convertCoordinate(point.lat), convertCoordinate(point.lon));
              latLng.push(coords);
            }
          });
          const polyLayer = L.polygon(latLng);
          polyLayer.addTo(map);
          this.drawnItems.addLayer(polyLayer);
          try {
            map.fitBounds(polyLayer.getBounds());
          } catch (error) {
            console.warn('Failed to fit polygon bounds:', error);
          }
          this.adhocService.setGeoJson(polyLayer.toGeoJSON());
        }
      });
  }

  onMapReady(map: L.Map): void {
    this.mapOperationsService.setMapInstance(map);

    setTimeout(() => {
      if (map && map.getContainer()) {
        try {
          map.invalidateSize(true);
          this.isMapReady = true;

          // Initialize layers if they exist
          const layers = this.rqaMapLayers();
          if (layers.length > 0) {
            this.handleLayersChanged(layers);
          }
        } catch (error) {
          console.warn('Failed to initialize map:', error);
        }
      }
    }, 300);
  }

  private handleLayersChanged(layers: AirspaceElement[]): void {
    this.selectedLayers = [];
    this.createLayers(layers);
    this.fetchLayersCount++;
  }

  ngAfterViewInit(): void {
    this.toolbarService.clearAction();
  }

  onMapClick(e: L.LeafletMouseEvent): void {
    // Don't reset styles if we're processing a layer click
    if (!this.isProcessingLayerClick) {
      this.mapLayerService.resetLayerStyles();
    }
    this.findIntersectingLayers(e.latlng);
    this.mapOperationsService.addMarker(e.latlng);
    this.activeAccordionIndex.set(this.selectedLayers.length === 1 ? 0 : null);
  }

  onHeightRangeChange(heightRange: string[]): void {
    this.heightRange = heightRange;
    const minHeight = heightRange[0];
    const maxHeight = heightRange[1];

    const map = this.mapOperationsService.mapInstance();
    if (map) {
      this.mapLayerService.filterLayersByHeightRange(minHeight, maxHeight, map);
    }
  }

  zoomIn(): void {
    this.mapOperationsService.zoomIn();
  }

  zoomOut(): void {
    this.mapOperationsService.zoomOut();
  }

  async onSearch(): Promise<void> {
    const coordinates = await this.mapOperationsService.openSearchDialog();
    if (coordinates) {
      this.mapOperationsService.addSearchMarker(coordinates);
    }
  }

  toggleFilters(): void {
    this.mapLayerService.toggleFilters();
  }

  trackByFn(index: number, layer: AirspaceElement): string {
    return layer.designator || `${index}-${layer.type || 'unknown'}`;
  }

  private createLayers(layers: AirspaceElement[]): void {
    const map = this.mapOperationsService.mapInstance();
    if (!map) return;

    const groupedLayers = this.rqaLayerCreatorService.createLayers(layers, map);

    this.rqaLayerCreatorService.setupLayerClickHandlers(groupedLayers, (layer, selectedLayers) => {
      this.selectedLayers = [];
      this.onLayerClick(layer);
      this.selectedLayers = selectedLayers;
    });

    this.mapLayerService.setGroupedLayers(groupedLayers);
    this.rqaLayerCreatorService.setupFilterLayerTypes(groupedLayers);
    this.rqaLayerCreatorService.applyLayerVisibility(map);
  }

  private handleToolbarAction(event: { action: MapToolbarAction; payload?: MapToolbarPayload } | null): void {
    if (event) {
      switch (event.action) {
        case 'zoomIn':
          this.mapOperationsService.zoomIn();
          break;
        case 'zoomOut':
          this.mapOperationsService.zoomOut();
          break;
        case 'search':
          this.onSearch();
          break;
        case 'toggleLayerGroup':
          if (event.payload) {
            const map = this.mapOperationsService.mapInstance();
            if (map) {
              this.mapLayerService.toggleLayerGroupVisibility(event.payload.visible, event.payload.groupId, map);
              this.mapLayerService.setDisabledAreas();
              this.mapLayerService.filterLayersByHeightRange(this.heightRange[0], this.heightRange[1], map);
              this.mapLayerService.bringToFrontLayers([
                MapLayerTypeEnum.RQA,
                MapLayerTypeEnum.ORDER,
                MapLayerTypeEnum.TEMPLATE
              ]);
            }
          }
          break;
        case 'filters':
          this.mapLayerService.toggleFilters();
      }
    }
  }

  onLayerClick(layer: L.GeoJSON): void {
    if (this.isProcessingLayerClick) {
      return;
    }

    this.isProcessingLayerClick = true;
    this.mapLayerService.resetLayerStyles();
    this.highlightLayer(layer);

    this.activeAccordionIndex.set(null);
    const map = this.mapOperationsService.mapInstance();
    if (map && this.isMapReady && map.getContainer()) {
      try {
        map.fitBounds(layer.getBounds(), { maxZoom: 8 });
      } catch (error) {
        console.warn('Failed to fit bounds:', error);
      }
    }

    setTimeout(() => {
      this.isProcessingLayerClick = false;
    }, 10);
  }

  private findIntersectingLayers(latlng: L.LatLng): void {
    const map = this.mapOperationsService.mapInstance();
    if (!map) return;

    this.selectedLayers = this.mapLayerService.findIntersectingLayers(latlng, map, (latLng, layer) =>
      this.mapOperationsService.isPointInLayer(latLng, layer)
    );

    if (this.selectedLayers.length === 1) {
      const groupedLayers = this.mapLayerService.groupedLayers();
      const layerType = this.selectedLayers[0].type;
      const layerGroup = groupedLayers[layerType];

      if (layerGroup) {
        layerGroup.eachLayer((layer) => {
          const layerOptions = (layer as L.GeoJSON).options as WebrqaGeoJSONOptions;
          if (layerOptions.designator === this.selectedLayers[0].designator) {
            this.onLayerClick(layer as L.GeoJSON);
            this.mapLayerService.bringToFrontLayers([
              MapLayerTypeEnum.RQA,
              MapLayerTypeEnum.ORDER,
              MapLayerTypeEnum.TEMPLATE
            ]);
          }
        });
      }
    }
  }

  private highlightLayer(layer: L.GeoJSON): void {
    const styles = (layer.options as WebrqaGeoJSONOptions).styles;
    this.mapLayerService.setLayerStyles(layer, styles, StyleType.HIGHLIGHT);
    layer.bringToFront();
  }

  private cleanDrawLayers(): void {
    const drawnItems = this.drawnItems.getLayers();
    if (drawnItems.length) {
      const map = this.mapOperationsService.mapInstance();
      if (map && this.isMapReady) {
        drawnItems.forEach((layer) => {
          map.removeLayer(layer);
        });
      }
    }
    const map = this.mapOperationsService.mapInstance();
    if (map && this.isMapReady && map.getContainer()) {
      try {
        map.setView(new L.LatLng(environment.center[0], environment.center[1]), 6);
      } catch (error) {
        console.warn('Failed to set map view:', error);
      }
    }
    this.adhocService.setGeoJson(null);
  }

  private resetAccordionState(): void {
    this.selectedLayers = [];
    this.activeAccordionIndex.set(null);
    this.mapLayerService.resetLayerStyles();
  }

  ngOnDestroy(): void {
    this.resetAccordionState();
  }

  onAccordionOpen(event: AccordionTabOpenEvent): void {
    this.handleAccordionToggle(event.index);
  }

  private handleAccordionToggle(index: number | null): void {
    if (index !== null) {
      const toggledLayer = this.selectedLayers[index];
      if (toggledLayer) {
        const groupedLayers = this.mapLayerService.groupedLayers();
        const layers = groupedLayers[toggledLayer.type]?.getLayers();
        if (layers) {
          layers.forEach((layer) => {
            const layerOptions = (layer as L.GeoJSON).options as WebrqaGeoJSONOptions;
            if (layerOptions.designator === toggledLayer.designator) {
              this.onLayerClick(layer as L.GeoJSON);
            }
          });
        }
      }
    } else {
      this.mapLayerService.resetLayerStyles();
    }
  }
}
